/**
 * Currency formatting utilities.
 * All functions accept a CurrencyCode and format values accordingly.
 * Values are assumed to be stored in MAD (base currency) and are
 * converted using the exchange rate before formatting.
 */
import { CurrencyCode, CURRENCY_CONFIGS } from '../context/CurrencyContext';

/**
 * Convert a value from MAD (base currency) to the target currency.
 */
export const convertFromMAD = (value: number, code: CurrencyCode): number => {
  return value * CURRENCY_CONFIGS[code].rateFromMAD;
};

/**
 * Format a number with currency symbol.
 * Full number with thousand separators, no decimals.
 * e.g. "MAD 1,234,567" or "1,234,567 €" or "$1,234,567"
 */
export const formatCurrency = (value: number | null | undefined, code: CurrencyCode): string => {
  if (value == null || isNaN(value)) return formatWithSymbol(0, code, 0);
  return formatWithSymbol(convertFromMAD(value, code), code, 0);
};

/**
 * Format a number in compact notation (K, M, B).
 * e.g. "MAD 1.2M", "$450K", "1.2M €"
 */
export const formatCurrencyCompact = (value: number | null | undefined, code: CurrencyCode): string => {
  if (value == null || isNaN(value)) return formatWithSymbol(0, code, 0);
  const converted = convertFromMAD(value ?? 0, code);
  const abs = Math.abs(converted);
  const sign = converted < 0 ? '-' : '';

  if (abs >= 1_000_000_000) {
    const num = (abs / 1_000_000_000).toFixed(1).replace(/\.0$/, '');
    return applySymbol(`${sign}${num}B`, code);
  }
  if (abs >= 1_000_000) {
    const num = (abs / 1_000_000).toFixed(1).replace(/\.0$/, '');
    return applySymbol(`${sign}${num}M`, code);
  }
  if (abs >= 1_000) {
    const num = (abs / 1_000).toFixed(1).replace(/\.0$/, '');
    return applySymbol(`${sign}${num}K`, code);
  }
  return formatWithSymbol(converted, code, 0);
};

/**
 * Format a number with currency symbol and 2 decimal places.
 * e.g. "MAD 1,234.56" or "1,234.56 €" or "$1,234.56"
 */
export const formatCurrencyPrecise = (value: number | null | undefined, code: CurrencyCode): string => {
  if (value == null || isNaN(value)) return formatWithSymbol(0, code, 2);
  return formatWithSymbol(convertFromMAD(value, code), code, 2);
};

/**
 * Return a parenthesized currency label for column headers.
 * e.g. "(MAD)", "(EUR)", "(USD)"
 */
export const currencyLabel = (code: CurrencyCode): string => {
  return `(${code})`;
};

// ── Internal helpers ──

function formatWithSymbol(value: number, code: CurrencyCode, decimals: number): string {
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return applySymbol(formatted, code);
}

function applySymbol(formatted: string, code: CurrencyCode): string {
  const config = CURRENCY_CONFIGS[code];
  if (config.symbolPosition === 'suffix') {
    return `${formatted} ${config.symbol}`;
  }
  return `${config.symbol} ${formatted}`;
}
